import axios from 'axios'
import OperationsRepository from '@/libs/customAxios/operationAxios'
import NightOpsRepository from '@/libs/customAxios/nightOpsAxios'
import ReportsRepository from '@/libs/customAxios/reportsAxios'
import ListsRepository from '@/libs/customAxios/listsAxios'

const sortingResource = '/sorting'
const sortingDeviceResource = '/sorting/devices'
const sortingBucketResource = '/sorting/buckets'
const defaultPagination = 20

export default {
  /**
   * Scan a waybill from a current node
   * @param {Object} payload - { waybill_number: string}
   */
  scanWaybill(payload) {
    return NightOpsRepository.post(`${sortingResource}/scan`, payload)
  },

  /**
   * Get stats for a particular bucket
   * @param {Number} bucketNodeId
   */
  getBucketStats(bucketNodeId) {
    return ReportsRepository.get(`${sortingResource}/stats/${bucketNodeId}`)
  },

  createBag(payload) {
    return OperationsRepository.post(`${sortingResource}/new-bag`, payload)
  },

  createReturnBag(payload) {
    return OperationsRepository.post('/sorting/new-return-bag', payload)
  },

  closeReturnBag(payload) {
    return OperationsRepository.post('/sorting/close-return-bag', payload)
  },

  getNextReturnBagId(merchantId, merchantBusinessId) {
    const businessQuery = merchantBusinessId ? `&merchant_business_id=${merchantBusinessId}` : ''
    return OperationsRepository.get(`/sorting/next-return-bag-id?merchant_id=${merchantId}${businessQuery}`)
  },

  async resetBucket(deviceIp) {
    return axios.get(`${deviceIp}/rest`)
  },

  fetchBagList(bucketNodeId, sortingIdString) {
    return ListsRepository.get(`${sortingResource}/bag-list?bucketNodeId=${bucketNodeId}&sortingIdString=${sortingIdString}`)
  },

  getBagDetails(bagId) {
    return ReportsRepository.get(`${sortingResource}/bag?sortingBagId=${bagId}`)
  },

  /**
   * Get stats for a particular bucket
   * @param {String} sortingIdString
   */
  getReportOverall(sortingIdString) {
    return ReportsRepository.get(`${sortingResource}/overall-stats/${sortingIdString}`)
  },

  getSessionStats(sortingIdString) {
    return ReportsRepository.get(`${sortingResource}/session/${sortingIdString}`)
  },

  getScanPerformance(sortingIdString) {
    return ReportsRepository.get(`${sortingResource}/scan-performance-tiles/${sortingIdString}`)
  },

  /**
   * Get stats for a particular bucket
   *
   */
  getReportOverallBuckets({ bucket_id, sorting_id_string }) {
    return ReportsRepository.get(`${sortingResource}/overall-bucket-stats`, {
      params: { bucket_id, sorting_id_string },
    })
  },

  /**
   * Get available buckets to scan into
   */
  getAvailableBuckets() {
    return ListsRepository.get(`${sortingResource}/buckets-available`)
  },

  getAvailableBucketsforReporting() {
    return ListsRepository.get(`${sortingResource}/buckets-available-reporting`)
  },

  /**
   * Login to a particular bucket node
   * @param {Object} payload - { bucket_node_id: number }
   */
  loginBucket(payload) {
    return OperationsRepository.post(`${sortingResource}/login-bucket`, payload)
  },

  /**
   * Logout from a particular bucket node
   * @param {Object} payload - { bucket_node_id: number }
   */
  logoutBucket(payload) {
    return OperationsRepository.post(`${sortingResource}/logout-bucket`, payload)
  },

  getCurrentBucketSession() {
    return OperationsRepository.get('/sorting/current-bucket')
  },

  getUserSessions(pageNumber = 1, pagination = defaultPagination) {
    return OperationsRepository.get(`/sorting/user-sessions?paginate=${pagination}&page=${pageNumber}`)
  },

  getBucketOrdersById(pageNumber = 1, pagination = defaultPagination, id, isReport = true, sortingString, filterQuery) {
    if (isReport) {
      return ListsRepository.get(`${sortingResource}/report/${id}/orders?paginate=${pagination}&page=${pageNumber}&sort=-id&sortingString=${sortingString}&${filterQuery}`)
    }
    return ListsRepository.get(`${sortingResource}/bucket/${id}/orders?paginate=${pagination}&page=${pageNumber}&sort=-id&sortingString=${sortingString}&${filterQuery}`)
  },

  getBucketOrdersNoPagination(id, isReport = true, sortingString) {
    if (isReport) {
      return ReportsRepository.get(`${sortingResource}/report/${id}/orders?sort=-id&noPagination&sortingString=${sortingString}`)
    }
    return ReportsRepository.get(`${sortingResource}/bucket/${id}/orders?sort=-id&noPagination&sortingString=${sortingString}`)
  },

  orderBulkDispatchStatusUpdate(payload) {
    return NightOpsRepository.post('/sorting/bulk-dispatch-update', payload)
  },

  orderBulkReturnStatusUpdate(payload) {
    return NightOpsRepository.post('/sorting/bulk-return-update', payload)
  },

  getSortingDevices(pageNumber = 1, pagination = defaultPagination, filterQuery) {
    return OperationsRepository.get(`${sortingDeviceResource}?paginate=${pagination}&page=${pageNumber}&sort=-id&${filterQuery}`)
  },

  getSortingDevicesNoPagination(filterQuery) {
    return OperationsRepository.get(`${sortingDeviceResource}?${filterQuery}&sort=-id&noPagination`)
  },

  getSortingBranches() {
    return ListsRepository.get(`${sortingDeviceResource}/branch-list`)
  },

  getEditUnassignedSortingBranches(nodeId) {
    return ListsRepository.get(`${sortingResource}/nodes/edit/unassigned?node_id=${nodeId}`)
  },

  getUnassignedSortingBranches() {
    return ListsRepository.get(`${sortingResource}/nodes/unassigned`)
  },

  createSortingDevice(payload) {
    return OperationsRepository.post(`${sortingDeviceResource}`, payload)
  },

  updateSortingDevice(device, payload) {
    return OperationsRepository.put(`${sortingDeviceResource}/${device}`, payload)
  },

  getSortingDeviceById(device) {
    return OperationsRepository.get(`${sortingDeviceResource}/${device}/device-data`)
  },

  unlinkDevice(device) {
    return OperationsRepository.post(`${sortingDeviceResource}/unlink/${device}`)
  },

  getDispatchReport(params) {
    return ReportsRepository.get('/sorting/report/dispatch-report', { params })
  },

  getToBeSortNewOrders(pageNumber = 1, pagination = defaultPagination, id) {
    return OperationsRepository.get(`${sortingResource}/bucket/to-be-sort-new-orders/${id}?paginate=${pagination}&page=${pageNumber}`)
  },

  getSortingActivityList(pageNumber = 1, pagination = defaultPagination, filterQuery) {
    return OperationsRepository.get(`${sortingResource}/audit-logs?paginate=${pagination}&page=${pageNumber}&sort=-id&${filterQuery}`)
  },

  getSortingActivityListNoPagination(filterQuery) {
    return OperationsRepository.get(`${sortingResource}/audit-logs?${filterQuery}&sort=-id&noPagination`)
  },

  getReturnReport(pageNumber = 1, pagination = defaultPagination, filterQuery) {
    return ReportsRepository.get(`/sorting/report/return-orders?paginate=${pagination}&page=${pageNumber}&${filterQuery}&sort=-id`)
  },

  getReturnReportNoPagination(filterQuery) {
    return ReportsRepository.get(`/sorting/report/return-orders?${filterQuery}&sort=-id&noPagination`)
  },

  getReturnBagReport(pageNumber = 1, pagination = defaultPagination, filterQuery) {
    return ReportsRepository.get(`/sorting/report/return-report?paginate=${pagination}&page=${pageNumber}&${filterQuery}`)
  },

  getReturnBagReportNoPagination(filterQuery) {
    return ReportsRepository.get(`/sorting/report/return-report?noPagination&${filterQuery}`)
  },

  getReturnBagOrders(bagId, pageNumber = 1, pagination = defaultPagination) {
    return ReportsRepository.get(`/sorting/return-bag/${bagId}/orders?paginate=${pagination}&page=${pageNumber}`)
  },

  getReturnBagOrdersNoPagination(bagId) {
    return ReportsRepository.get(`/sorting/return-bag/${bagId}/orders?noPagination`)
  },

  getActiveReturnBag(merchantId, merchantBusinessId) {
    const businessQuery = merchantBusinessId ? `&merchant_business_id=${merchantBusinessId}` : ''
    return OperationsRepository.get(`/sorting/active-return-bag?merchant_id=${merchantId}${businessQuery}`)
  },

  getSortingBuckets(pageNumber = 1, pagination = defaultPagination, filterQuery) {
    return OperationsRepository.get(`${sortingBucketResource}?paginate=${pagination}&page=${pageNumber}&sort=-id&${filterQuery}`)
  },

  getSortingBucketsNoPagination(filterQuery) {
    return OperationsRepository.get(`${sortingBucketResource}?${filterQuery}&sort=-id&noPagination`)
  },

  getLastBuckets() {
    return ListsRepository.get(`${sortingBucketResource}/last-buckets`)
  },

  getUnassignedBranches() {
    return ListsRepository.get(`${sortingBucketResource}/branch-list`)
  },

  getAssignedBranches() {
    return ListsRepository.get(`${sortingBucketResource}/existing-branch-list`)
  },

  createSortingBucket(payload) {
    return OperationsRepository.post(`${sortingBucketResource}`, payload)
  },

  getSortingBucketDetails(bucket) {
    return OperationsRepository.get(`${sortingBucketResource}/${bucket}/bucket-data`)
  },

  updateSortingBucket(bucket, payload) {
    return OperationsRepository.put(`${sortingBucketResource}/${bucket}`, payload)
  },
}
